import React, { useState } from 'react';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Tag from '@amzn/meridian/tag';
import ButtonGroup, { ButtonOption } from '@amzn/meridian/button-group';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import { EosShiftData, EosPathPerf } from './eosReportingData';
import { formatWholeNumber } from '../utils';
import {
  deltaPctToStatus,
  hcDeltaToStatus,
  signedDeltaPct,
  signedDelta,
  statusToTagType,
  EosStatus,
} from './eosReportingUtils';

/**
 * Process Path Performance table for one shift (v6). A Meridian ButtonGroup
 * toggles Primary/Support; Primary shows Volume + Rate + HC, Support shows
 * Rate + HC. Each metric cell shows `actual / target` followed inline by a
 * status-colored Meridian Tag delta, left-aligned — matching the v6 mock.
 * Presentational.
 */
export interface EosPathTableProps {
  shift: EosShiftData;
}

type PathType = 'primary' | 'support';

/** `actual / target` value with a status-colored delta Tag inline after it
 *  (matches the v6 mock: value then a small soft-tinted pill on one line). */
const MetricCell: React.FC<{ value: string; delta: string; status: EosStatus }> = ({
  value,
  delta,
  status,
}) => (
  <Row spacing="200" widths="fit" alignmentVertical="center" wrap="down">
    <Text type="b300">{value}</Text>
    <Tag type={statusToTagType[status]}>{delta}</Tag>
  </Row>
);

const EosPathTable: React.FC<EosPathTableProps> = ({ shift }) => {
  const [pathType, setPathType] = useState<PathType>('primary');
  const isSupport = pathType === 'support';
  const paths: EosPathPerf[] = isSupport ? shift.supportPaths : shift.primaryPaths;

  return (
    <Column spacing="300" data-testid={`eos-path-${shift.key}`}>
      <Row alignmentHorizontal="justify" alignmentVertical="center">
        <Text type="h100">{shift.label} — Process Path Performance</Text>
        <ButtonGroup
          value={pathType}
          onChange={(value: string) => setPathType(value as PathType)}
          size="small"
        >
          <ButtonOption value="primary">Primary Paths</ButtonOption>
          <ButtonOption value="support">Support Paths</ButtonOption>
        </ButtonGroup>
      </Row>

      <Box type="outline" spacingInset="400">
        <Table headerRows={1} headerColumns={1} showDividers spacing="small">
        <TableRow>
          <TableCell header>Process Path</TableCell>
          {!isSupport && <TableCell header>Volume</TableCell>}
          <TableCell header>Rate (UPH)</TableCell>
          <TableCell header>HC</TableCell>
        </TableRow>

        {paths.map((p, i) => {
          const volStatus = deltaPctToStatus(
            (((p.vol ?? 0) - (p.volTgt ?? 0)) / (p.volTgt ?? 0)) * 100
          );
          const rateStatus = deltaPctToStatus(((p.rate - p.rateTgt) / p.rateTgt) * 100);
          const hcStatus = hcDeltaToStatus(p.hc - p.hcTgt, p.hcTgt);
          return (
            <TableRow key={`${p.name}-${i}`} highlightOnHover>
              <TableCell header>
                <Text type="b300">{p.name}</Text>
              </TableCell>
              {!isSupport && (
                <TableCell>
                  <MetricCell
                    value={`${formatWholeNumber(p.vol ?? 0)} / ${formatWholeNumber(p.volTgt ?? 0)}`}
                    delta={signedDeltaPct(p.vol ?? 0, p.volTgt ?? 0)}
                    status={volStatus}
                  />
                </TableCell>
              )}
              <TableCell>
                <MetricCell
                  value={`${p.rate.toLocaleString()} / ${p.rateTgt.toLocaleString()}`}
                  delta={signedDeltaPct(p.rate, p.rateTgt)}
                  status={rateStatus}
                />
              </TableCell>
              <TableCell>
                <MetricCell
                  value={`${p.hc} / ${p.hcTgt}`}
                  delta={signedDelta(p.hc, p.hcTgt)}
                  status={hcStatus}
                />
              </TableCell>
            </TableRow>
          );
        })}
        </Table>
      </Box>
    </Column>
  );
};

export default EosPathTable;
